import React, { ReactNode, useEffect, useRef } from 'react';
import { BackHandler, Platform, Pressable, useWindowDimensions, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faCircleChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import Label from '../Label';

interface Props {
  missionName: string;
  summary?: ReactNode;
  page: 'review';
  presentation?: 'page' | 'footer' | 'embedded';
  onBack(): void;
  onNavigate?(): void;
  children: ReactNode;
}

/** A subpage of the existing details panel; the surrounding map remains interactive. */
const MissionPanelPage = ({ missionName, summary, page, presentation = 'page', onBack, onNavigate, children }: Props) => {
  const theme = useCustomTheme();
  const t = useTranslation();
  const { width } = useWindowDimensions();
  const panel = useRef<View>(null);
  const back = useRef(onBack);
  back.current = onBack;
  const linkColor = theme.text.titleLight?.toString() ?? theme.text.primary.toString();

  useEffect(() => {
    const previous = Platform.OS === 'web' && typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;
    if (Platform.OS !== 'web') {
      const subscription = BackHandler.addEventListener('hardwareBackPress', () => { back.current(); return true; });
      return () => subscription.remove();
    }
    if (typeof document === 'undefined') return;
    const keyboard = (event: KeyboardEvent) => {
      const root = panel.current as unknown as HTMLElement | null;
      if (event.key === 'Escape' && !event.defaultPrevented && root?.contains(document.activeElement)) {
        event.preventDefault();
        back.current();
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => { document.removeEventListener('keydown', keyboard); previous?.focus?.({ preventScroll: true }); };
  }, []);

  useEffect(() => {
    onNavigate?.();
    if (Platform.OS === 'web') (panel.current as unknown as HTMLElement | null)?.focus?.({ preventScroll: true });
  }, [page, onNavigate]);

  return <View ref={panel} testID={`mission-panel-${page}`} tabIndex={-1}
    style={{ width: '100%', maxWidth: presentation === 'page' ? 640 : undefined, alignSelf: 'center', minWidth: 0,
      padding: presentation === 'page' ? width < 768 ? 16 : 24 : 0 }}>
    {presentation === 'page' && <><Pressable testID="mission-panel-back" accessibilityRole="button" accessibilityLabel={t('back')} onPress={onBack}
      style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', minHeight: 44, paddingRight: 16 }}>
      <FontAwesomeIcon icon={faCircleChevronLeft} size={24} color={linkColor} />
      <Label numberOfLines={0} style={{ color: linkColor, marginLeft: 8 }}>{t('back')}</Label>
    </Pressable>
    {summary ?? <Label accessibilityRole="header" numberOfLines={0} style={{ marginTop: 16, fontSize: 18, lineHeight: 24, fontWeight: '700' }}>{missionName}</Label>}</>}
    {children}
  </View>;
};

export default MissionPanelPage;
